/**
 * theme-slider-unified.js
 * Unified slider functionality
 * 
 * Size: ~25KB minified
 * Load: Asynchronous, non-blocking
 * Triggers: .unified-slider, [data-slider="unified"] detected
 * Dependencies: theme-slider-base.js
 */

import { createSlider } from '../modules/slider/unified-slider.js';
import { createSliderControls } from '../modules/slider/slider-utils.js';

// Prevent duplicate initialization
if (window.themeSliderUnified) {
  console.log('[Unified Slider] Already loaded - skipping duplicate');
} else {
  console.log('[Unified Slider] Loaded');

  /**
   * Auto-initialize unified sliders (data-slider="unified" pattern)
   */
  function initUnifiedSliders() {
    const sliders = document.querySelectorAll('[data-slider="unified"]:not([data-initialized])');
    
    if (sliders.length === 0) {
      console.log('[Unified Slider] No data-slider sliders found');
    } else {
      console.log(`[Unified Slider] Initializing ${sliders.length} data-slider slider(s)`);

      sliders.forEach(slider => {
        try {
          const config = {
            containerSelector: slider,
            slideSelector: slider.dataset.slideSelector || '.slide',
            mode: slider.dataset.mode || 'linear',
            autoplay: slider.dataset.autoplay ? JSON.parse(slider.dataset.autoplay) : {},
            responsive: slider.dataset.responsive ? JSON.parse(slider.dataset.responsive) : {},
            stackSettings: slider.dataset.stackSettings ? JSON.parse(slider.dataset.stackSettings) : {},
            duration: slider.dataset.duration ? parseFloat(slider.dataset.duration) : 1.6,
            easing: slider.dataset.easing || 'expo.inOut',
          };

          const instance = createSlider(config);
          slider.setAttribute('data-initialized', 'true');

          // Wire up slider controls if slider has an ID
          if (instance && slider.id) {
            createSliderControls('#' + slider.id, instance);
          }
        } catch (error) {
          console.error('[Unified Slider] Initialization error:', error, slider);
        }
      });
    }

    // Also initialize G2N block sliders (WordPress block pattern)
    initG2NSliders();
  }

  /**
   * Initialize G2N block sliders (data-g2n-slider pattern)
   * These use JSON config in the data attribute
   */
  function initG2NSliders() {
    const sliders = document.querySelectorAll('[data-g2n-slider]:not([data-g2n-initialized])');
    
    if (sliders.length === 0) {
      return;
    }

    console.log(`[Unified Slider] Initializing ${sliders.length} G2N slider(s)`);

    sliders.forEach(element => {
      try {
        const configData = element.getAttribute('data-g2n-slider');
        if (!configData) return;

        const config = JSON.parse(configData);
        const sliderInstance = createSlider(config);

        if (sliderInstance) {
          element.setAttribute('data-g2n-initialized', 'true');
          // Expose the instance on the element so blocks (e.g. shi-timeline)
          // can drive the slider without scanning the global registry.
          element._sliderInstance = sliderInstance;
          setupG2NAccessibility(element, sliderInstance, config);
          console.log('[Unified Slider] G2N slider initialized:', element.id);
        }
      } catch (error) {
        console.error('[Unified Slider] G2N initialization error:', error, element);
      }
    });
  }

  /**
   * Setup accessibility features for G2N block sliders
   */
  function setupG2NAccessibility(element, sliderInstance, config) {
    // Handle slide change announcements
    if (config.accessibility?.announceSlideChanges) {
      const announcer = element.querySelector(`#${element.id}-announcer`);
      if (announcer) {
        element.addEventListener('slideChange', (e) => {
          const slideNumber = e.detail.index + 1;
          const totalSlides = sliderInstance.totalSlides || 0;
          announcer.textContent = `Slide ${slideNumber} of ${totalSlides}`;
        });
      }
    }

    // Keyboard navigation
    if (config.accessibility?.enableKeyboardNavigation && !element.hasAttribute('data-keyboard-enabled')) {
      element.addEventListener('keydown', (e) => {
        switch (e.key) {
          case 'ArrowLeft':
          case 'ArrowUp':
            e.preventDefault();
            sliderInstance.goToPrevious();
            break;
          case 'ArrowRight':
          case 'ArrowDown':
            e.preventDefault();
            sliderInstance.goToNext();
            break;
          case 'Home':
            e.preventDefault();
            sliderInstance.goToSlide(0);
            break;
          case 'End':
            e.preventDefault();
            sliderInstance.goToSlide(sliderInstance.totalSlides - 1);
            break;
        }
      });

      if (!element.hasAttribute('tabindex')) {
        element.setAttribute('tabindex', '0');
      }
    }
  }

  // Make globally available
  window.createSlider = createSlider;
  window.initG2NSliders = initG2NSliders; // For AJAX/dynamic content
  window.themeSliderUnified = {
    init: initUnifiedSliders,
    initG2N: initG2NSliders,
    createSlider,
  };

  // Auto-initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initUnifiedSliders);
  } else {
    initUnifiedSliders();
  }
}